// ===========================================
// Class Types Management Page
// ===========================================
// CRUD interface for managing class types

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ColorPickerField from '../../components/common/ColorPickerField';
import { defaultLessonTypeColors, getContrastTextColor } from '../../styles/brandColors';
import {
  useLessonTypes,
  useCreateLessonType,
  useUpdateLessonType,
  useDeleteLessonType,
} from '../../hooks/useAdmin';
import { LessonType } from '../../api/admin.api';

// ===========================================
// TYPES
// ===========================================

type LessonTypeCategory = 'INDIVIDUAL' | 'GROUP' | 'BAND' | 'HYBRID';

const lessonTypeOptions: { value: LessonTypeCategory; label: string }[] = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'GROUP', label: 'Group' },
  { value: 'BAND', label: 'Band' },
  { value: 'HYBRID', label: 'Hybrid' },
];

// ===========================================
// COMPONENT
// ===========================================

export default function LessonTypesPage() {
  const { data: lessonTypes, isLoading, error } = useLessonTypes();
  const createMutation = useCreateLessonType();
  const updateMutation = useUpdateLessonType();
  const deleteMutation = useDeleteLessonType();

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLessonType, setEditingLessonType] = useState<LessonType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'INDIVIDUAL' as LessonTypeCategory,
    defaultDuration: '45',
    description: '',
    isActive: true,
    color: null as string | null,
    allowPartnerOverlap: false,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [lessonTypeToDelete, setLessonTypeToDelete] = useState<LessonType | null>(null);

  // Filter lesson types
  const filteredLessonTypes = useMemo(() => {
    if (!lessonTypes) return [];

    if (hideInactive) {
      return lessonTypes.filter((lt) => lt.isActive);
    }

    return lessonTypes;
  }, [lessonTypes, hideInactive]);

  // Table columns
  const columns: Column<LessonType>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 150,
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 120,
      format: (value, row) => {
        const typeValue = value as LessonTypeCategory;
        const typeInfo = lessonTypeOptions.find((t) => t.value === typeValue);
        const displayColor = row.color || defaultLessonTypeColors[typeValue] || '#ccc';
        return (
          <Chip
            label={typeInfo?.label ?? String(value)}
            size="small"
            sx={{
              bgcolor: displayColor,
              color: getContrastTextColor(displayColor),
            }}
          />
        );
      },
    },
    {
      id: 'defaultDuration',
      label: 'Duration',
      minWidth: 100,
      format: (value) => `${value} min`,
    },
    {
      id: 'description',
      label: 'Description',
      minWidth: 200,
      format: (value) => (value as string) || '-',
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 100,
      align: 'right',
      format: (value) =>
        value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        ),
    },
  ];

  // Handlers
  const handleAdd = () => {
    setEditingLessonType(null);
    setFormData({
      name: '',
      type: 'INDIVIDUAL',
      defaultDuration: '45',
      description: '',
      isActive: true,
      color: null,
      allowPartnerOverlap: false,
    });
    setModalOpen(true);
  };

  const handleEdit = (lessonType: LessonType) => {
    setEditingLessonType(lessonType);
    setFormData({
      name: lessonType.name,
      type: lessonType.type,
      defaultDuration: lessonType.defaultDuration.toString(),
      description: lessonType.description || '',
      isActive: lessonType.isActive,
      color: lessonType.color,
      allowPartnerOverlap: lessonType.allowPartnerOverlap,
    });
    setModalOpen(true);
  };

  const handleDelete = (lessonType: LessonType) => {
    setLessonTypeToDelete(lessonType);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingLessonType) {
        await updateMutation.mutateAsync({
          id: editingLessonType.id,
          data: {
            name: formData.name,
            type: formData.type,
            defaultDuration: parseInt(formData.defaultDuration, 10),
            description: formData.description || undefined,
            isActive: formData.isActive,
            color: formData.color,
            // Only meaningful for INDIVIDUAL; the server coerces others to false regardless.
            allowPartnerOverlap: formData.type === 'INDIVIDUAL' ? formData.allowPartnerOverlap : false,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          type: formData.type,
          defaultDuration: parseInt(formData.defaultDuration, 10),
          description: formData.description || undefined,
          color: formData.color ?? undefined,
          allowPartnerOverlap: formData.type === 'INDIVIDUAL' ? formData.allowPartnerOverlap : false,
        });
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  // Check if lesson type can be deleted (must be inactive)
  const canDeleteLessonType = (lessonType: LessonType) => !lessonType.isActive;

  const handleConfirmDelete = async () => {
    if (lessonTypeToDelete) {
      try {
        await deleteMutation.mutateAsync(lessonTypeToDelete.id);
        setDeleteDialogOpen(false);
        setLessonTypeToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Class Types"
        subtitle="Manage available class types and their configurations"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Class Types' },
        ]}
        actionLabel="Add Class Type"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load class types. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive class types"
        />

        {filteredLessonTypes.length !== (lessonTypes?.length ?? 0) && (
          <Chip
            label={`Showing ${filteredLessonTypes.length} of ${lessonTypes?.length ?? 0} class types`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <DataTable
        columns={columns}
        data={filteredLessonTypes}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canDelete={canDeleteLessonType}
        deleteDisabledTooltip="Set status to Inactive to delete this class type"
        emptyMessage={
          lessonTypes && lessonTypes.length > 0
            ? 'No class types match the current filter.'
            : "No class types configured. Click 'Add Class Type' to create one."
        }
        searchPlaceholder="Search class types..."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingLessonType ? 'Edit Class Type' : 'Add Class Type'}
        submitLabel={editingLessonType ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            placeholder="e.g., Piano Individual"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as LessonTypeCategory })
              }
              label="Type"
            >
              {lessonTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Default Duration (minutes)"
            type="number"
            value={formData.defaultDuration}
            onChange={(e) => setFormData({ ...formData, defaultDuration: e.target.value })}
            required
            fullWidth
            inputProps={{ min: 15, step: 15 }}
          />
          <TextField
            label="Description"
            placeholder="Describe this class type..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <ColorPickerField
            label="Color"
            value={formData.color}
            onChange={(color) => setFormData({ ...formData, color })}
            helperText={`Optional color override. Default: ${lessonTypeOptions.find((t) => t.value === formData.type)?.label || formData.type} uses ${defaultLessonTypeColors[formData.type]}`}
            useSchoolPalette
          />
          {formData.type === 'INDIVIDUAL' && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowPartnerOverlap}
                  onChange={(e) => setFormData({ ...formData, allowPartnerOverlap: e.target.checked })}
                />
              }
              label="Allow partner classes"
              // Explains the feature so admins understand what enabling it permits.
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
            />
          )}
          {formData.type === 'INDIVIDUAL' && (
            <Alert severity="info" sx={{ mt: -1 }}>
              When enabled, two individual classes of this type for the same teacher can be paired to
              overlap (e.g. one 4:00–5:00, another 4:30–5:30). You choose the specific partner when
              creating or editing a class.
            </Alert>
          )}
          {editingLessonType && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Class Type"
        message={`Are you sure you want to delete "${lessonTypeToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
